import { Bell, Bike, Gauge, HelpCircle, Home, LineChart, Map, MapPinned, Medal, Route, ShieldCheck, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { subscribeMyPendingActionsSummary } from "../data/actionsService";
import { getManagementUserProfile } from "../data/eventService";
import { useI18n } from "../i18n";
import { NavSection } from "./NavSection";
import { ProfileHeader } from "./ProfileHeader";

const navClass = ({ isActive }: { isActive: boolean }) => `nav-link ${isActive ? "active" : ""}`;

function NavText({ badge, children }: { badge?: number; children: string }) {
  return (
    <span className="nav-link-label">
      <span>{children}</span>
      {badge && badge > 0 ? <span className="nav-badge">{badge > 99 ? "99+" : badge}</span> : null}
    </span>
  );
}

export function Sidebar() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [pendingActionsCount, setPendingActionsCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      setPendingActionsCount(0);
      return;
    }

    return subscribeMyPendingActionsSummary(user, (summary) => {
      setPendingActionsCount(summary.error ? 0 : summary.total);
    });
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    async function loadAdminAccess() {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        const profile = await getManagementUserProfile(user);
        if (!cancelled) setIsAdmin(profile.isAdmin);
      } catch {
        if (!cancelled) setIsAdmin(false);
      }
    }

    loadAdminAccess();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <aside className="sidebar">
      <div className="brand">
        <img src="/logo.png" alt="" />
        <div>
          <strong>PULS</strong>
          <span>Web Control Center</span>
        </div>
      </div>

      <ProfileHeader />

      <NavSection title={t("nav.home")}>
        <NavLink className={navClass} to="/">
          <Home size={18} />
          {t("nav.dashboard")}
        </NavLink>
      </NavSection>

      <NavSection title={t("nav.brevetActions")}>
        <NavLink className={navClass} to="/events">
          <Bike size={18} />
          {t("nav.actions")}
        </NavLink>
        <NavLink className={navClass} to="/my-events">
          <Users size={18} />
          {t("nav.myActions")}
        </NavLink>
      </NavSection>

      <NavSection title={t("nav.routes")}>
        <NavLink className={navClass} to="/routes">
          <Route size={18} />
          {t("nav.myRoutes")}
        </NavLink>
        <NavLink className={navClass} to="/route-editor">
          <MapPinned size={18} />
          {t("nav.routeEditor")}
        </NavLink>
      </NavSection>

      <NavSection title={t("nav.community")}>
        <NavLink className={navClass} to="/community/routes">
          <Route size={18} />
          {t("nav.communityRoutes")}
        </NavLink>
        <NavLink className={navClass} to="/community/segments">
          <Map size={18} />
          {t("nav.segments")}
        </NavLink>
        <NavLink className={navClass} to="/segment-leaderboards">
          <Medal size={18} />
          {t("nav.segmentRankings")}
        </NavLink>
      </NavSection>

      <NavSection title={t("nav.tasks")}>
        <NavLink className={navClass} to="/actions">
          <Bell size={18} />
          <NavText badge={pendingActionsCount}>{t("nav.tasks")}</NavText>
        </NavLink>
        <NavLink className={navClass} to="/actions?tab=invitations">
          <Bell size={18} />
          {t("nav.invitations")}
        </NavLink>
        <NavLink className={navClass} to="/actions?tab=sos">
          <HelpCircle size={18} />
          {t("nav.sosHelp")}
        </NavLink>
        <NavLink className={navClass} to="/actions?tab=support">
          <ShieldCheck size={18} />
          {t("nav.supportControl")}
        </NavLink>
      </NavSection>

      {isAdmin ? (
        <NavSection title={t("nav.adminSection")}>
          <NavLink className={navClass} to="/admin">
            <ShieldCheck size={18} />
            {t("nav.admin")}
          </NavLink>
        </NavSection>
      ) : null}

      <NavSection title={t("nav.settings")}>
        <NavLink className={navClass} to="/profile">
          <Gauge size={18} />
          {t("nav.profile")}
        </NavLink>
        <NavLink className={navClass} to="/segments/preview">
          <LineChart size={18} />
          {t("nav.segmentDetail")}
        </NavLink>
      </NavSection>
    </aside>
  );
}
